// src/shared/ui/Alert.jsx
//
// Generalizes the border/bg/icon banner structure golive's
// PipelineStatusAlert.jsx built for its own status vocabulary — this is the
// generic primitive underneath that kind of thing: a fixed set of tone
// variants instead of a per-status color/icon map. Modules with their own
// status vocabulary (like golive's pipeline states) keep their own mapping
// component but can render through this for the actual banner markup.
//
// Usage:
//   <Alert variant="warning" title="Pipeline paused for review">Approve to continue.</Alert>
//   <Alert variant="danger" title="Deployment failed">{errorMessage}</Alert>

import PropTypes from "prop-types";
import { CheckCircle2, AlertTriangle, XCircle, Info } from "lucide-react";

const VARIANTS = {
  info: { border: "border-brand-100 bg-brand-50", bar: "bg-status-info", iconCls: "text-status-info", Icon: Info },
  success: { border: "border-green-100 bg-green-50", bar: "bg-status-good", iconCls: "text-status-good", Icon: CheckCircle2 },
  warning: { border: "border-amber-100 bg-amber-50", bar: "bg-status-warn", iconCls: "text-status-warn", Icon: AlertTriangle },
  danger: { border: "border-red-100 bg-red-50", bar: "bg-status-critical", iconCls: "text-status-critical", Icon: XCircle },
  neutral: { border: "border-line bg-gray-50", bar: "bg-gray-400", iconCls: "text-muted", Icon: Info },
};

export default function Alert({ variant = "info", icon, title, children, className = "" }) {
  const config = VARIANTS[variant] ?? VARIANTS.info;
  const Icon = icon ?? config.Icon;

  return (
    <div className={`flex gap-4 rounded-card border p-4 items-start ${config.border} ${className}`.trim()}>
      <div className={`w-1 self-stretch rounded-full shrink-0 ${config.bar}`} />
      <Icon size={18} className={`shrink-0 mt-0.5 ${config.iconCls}`} />
      <div className="min-w-0 flex-1">
        {title && <p className="text-sm font-semibold text-ink">{title}</p>}
        {children && <div className="mt-0.5 text-sm text-muted">{children}</div>}
      </div>
    </div>
  );
}

Alert.propTypes = {
  variant: PropTypes.oneOf(["info", "success", "warning", "danger", "neutral"]),
  icon: PropTypes.elementType,
  title: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
};
